<template>
    <div class="status-bar" v-if="!loading">
        <div class="results-count">
            {{ resultsLabel }}
        </div>

        <div class="active-filters" v-if="isFiltered">
            <span class="filter-label">Active filters:</span>
            <div class="filter-tags">
                <div class="filter-tag" v-if="searchStr">
                    Search: {{ searchStr }}
                    <button class="tag-remove" type="button" @click="$emit('clear-search')">x</button>
                </div>
                <div class="filter-tag" v-if="searchType">
                    Search Type: {{ getSearchTypeLabel(searchType) }}
                    <button class="tag-remove" type="button" @click="clearSearchType">x</button>
                </div>
                <div class="filter-tag" v-if="filters.groupId">
                    Group: {{ getGroupName(filters.groupId) }}
                    <button class="tag-remove" type="button" @click="clearFilter('groupId', '')">x</button>
                </div>
                <div class="filter-tag" v-if="filters.dateFrom || filters.dateTo">
                    Date: {{ filters.dateFrom || 'Any' }} - {{ filters.dateTo || 'Any' }}
                    <button class="tag-remove" type="button" @click="clearDates">x</button>
                </div>
                <div class="filter-tag" v-if="filters.status">
                    Status: {{ filters.status === 'active' ? 'Active' : 'Inactive' }}
                    <button class="tag-remove" type="button" @click="clearFilter('status', '')">x</button>
                </div>
                <div class="filter-tag" v-if="filters.verified">
                    Verified: {{ filters.verified === 'true' ? 'Yes' : 'No' }}
                    <button class="tag-remove" type="button" @click="clearFilter('verified', '')">x</button>
                </div>
                <div class="filter-tag" v-if="filters.minPoints !== null && filters.minPoints !== ''">
                    Min Points: {{ filters.minPoints }}
                    <button class="tag-remove" type="button" @click="clearFilter('minPoints', null)">x</button>
                </div>
                <div class="filter-tag" v-if="filters.maxPoints !== null && filters.maxPoints !== ''">
                    Max Points: {{ filters.maxPoints }}
                    <button class="tag-remove" type="button" @click="clearFilter('maxPoints', null)">x</button>
                </div>
                <div class="filter-tag" v-if="sortField !== 'createdAt' || sortDirection !== 'desc'">
                    Sort: {{ formatSortFieldLabel(sortField) }} ({{ sortDirection === 'asc' ? 'A-Z' : 'Z-A' }})
                    <button class="tag-remove" type="button" @click="$emit('reset-sort')">x</button>
                </div>
                <div class="filter-tag" v-if="secondarySortField && (secondarySortField !== 'last_name' || secondarySortDirection !== 'asc')">
                    Secondary sort: {{ formatSortFieldLabel(secondarySortField) }} ({{ secondarySortDirection === 'asc' ? 'A-Z' : 'Z-A' }})
                    <button class="tag-remove" type="button" @click="$emit('reset-secondary-sort')">x</button>
                </div>
                <button class="btn btn-sm btn-outline-secondary ms-2" type="button" @click="$emit('reset-filters')" v-if="isFiltered">
                    Clear All
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        loading: { type: Boolean, default: false },
        totalCount: { type: Number, default: 0 },
        page: { type: Number, default: 1 },
        limit: { type: Number, default: 25 },
        isFiltered: { type: Boolean, default: false },
        searchStr: { type: String, default: "" },
        searchType: { type: String, default: "" },
        filterOptions: { type: Object, required: true },
        filters: { type: Object, required: true },
        customerGroups: { type: Array, default: () => [] },
        sortField: { type: String, default: "createdAt" },
        sortDirection: { type: String, default: "desc" },
        secondarySortField: { type: String, default: "" },
        secondarySortDirection: { type: String, default: "asc" },
        formatSortFieldLabel: { type: Function, required: true },
    },
    computed: {
        resultsLabel() {
            if (!this.totalCount) return "No customers found"
            const start = (this.page - 1) * this.limit + 1
            const end = Math.min(this.page * this.limit, this.totalCount)
            return `Showing ${start} - ${end} of ${this.totalCount} customers`
        },
    },
    methods: {
        getGroupName(groupId) {
            const group = this.customerGroups.find((item) => item.id === groupId)
            return group ? group.name : "Unknown Group"
        },
        getSearchTypeLabel(type) {
            const option = this.filterOptions.searchType.find((item) => item.value === type)
            return option ? option.label : "Unknown Type"
        },
        clearFilter(field, value) {
            this.$emit("update-filter", { field, value })
            this.$emit("refresh")
        },
        clearSearchType() {
            this.$emit("update:searchType", "")
            this.$emit("refresh")
        },
        clearDates() {
            this.$emit("update-filter", { field: "dateFrom", value: null })
            this.$emit("update-filter", { field: "dateTo", value: null })
            this.$emit("refresh")
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .results-count {
        font-size: 14px;
        color: $textColor;
        opacity: 0.7;
    }

    .active-filters {
        display: flex;
        align-items: center;
        gap: 8px;

        .filter-label {
            font-size: 14px;
            color: $textColor;
            opacity: 0.7;
        }

        .filter-tags {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;

            .filter-tag {
                display: flex;
                align-items: center;
                background-color: rgba($primaryColor, 0.05);
                border: 1px solid rgba($primaryColor, 0.1);
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 13px;
                color: $textColor;

                .tag-remove {
                    margin-left: 6px;
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    color: $textColor;
                    opacity: 0.5;
                    line-height: 1;
                    font-size: 16px;

                    &:hover {
                        opacity: 0.8;
                    }
                }
            }

            .btn-outline-secondary {
                font-size: 13px;
                padding: 4px 8px;
                border-radius: 4px;
                border: 1px solid $lightBorderColor;
                background: none;
                color: $textColor;

                &:hover {
                    background-color: $lightHoverColor;
                }
            }
        }
    }
}

:deep(.dark) {
    .status-bar {
        .results-count,
        .filter-label {
            color: rgba($darkTextColor, 0.8);
        }

        .filter-tags {
            .filter-tag {
                background-color: rgba($secondaryColorDark, 0.1);
                border-color: rgba($secondaryColorDark, 0.2);
                color: $darkTextColor;

                .tag-remove {
                    color: $darkTextColor;
                }
            }

            .btn-outline-secondary {
                border-color: $darkBorderColor;
                color: $darkTextColor;

                &:hover {
                    background-color: rgba(255, 255, 255, 0.05);
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .status-bar {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>
